import cn from 'classnames';
// Constants
import { INFO } from 'constants/external-links';
// Hooks
import { useOnboardingContext } from 'hooks';
// Components
import Text from 'ui3/Text/Text';
import Button from 'ui3/Button/Button';
import LinkButton from 'ui3/LinkButton/LinkButton';
// Styles
import styles from './BecomeACreatorCTA.module.scss';

type BecomeACreatorCTAProps = {
  className?: string;
  onGoProClick?: () => void;
  onLearnMoreClick?: () => void;
  learnMoreLink?: string;
  variant?: 'standalone' | 'inline';
};

const BecomeACreatorCTA = ({
  className,
  onGoProClick,
  onLearnMoreClick,
  learnMoreLink = INFO,
  variant = 'standalone',
}: BecomeACreatorCTAProps) => {
  const isInline = variant === 'inline';
  const { setShowOnboardingModal } = useOnboardingContext();

  const handleGoProClick = () => {
    if (onGoProClick) {
      onGoProClick();
    } else {
      setShowOnboardingModal(true);
    }
  };

  const renderGoProButton = (buttonClassName?: string) => {
    const commonProps = {
      variant: 'secondary' as const,
      icon: 'crown' as const,
      className: cn(styles.goProButton, buttonClassName),
      'aria-label': 'Go Pro - Become a MILLIONS Creator',
    };

    return (
      <Button {...commonProps} onClick={handleGoProClick}>
        Go Pro
      </Button>
    );
  };

  const renderLearnMoreButton = (buttonClassName?: string) => {
    const commonProps = {
      variant: 'secondary' as const,
      subVariant: 'text' as const,
      icon: 'arrowRight' as const,
      iconPosition: 'right' as const,
      className: cn(styles.learnMoreButton, buttonClassName),
      'aria-label': 'Learn more about becoming a MILLIONS Creator',
    };

    return onLearnMoreClick ? (
      <Button {...commonProps} onClick={onLearnMoreClick}>
        Learn More
      </Button>
    ) : (
      <LinkButton {...commonProps} link={learnMoreLink}>
        Learn More
      </LinkButton>
    );
  };

  return (
    <div className={cn(styles.root, className, isInline && styles.inline)}>
      <div className={styles.card}>
        {/* Mobile Layout */}
        <div className={styles.mobileLayout}>
          <div className={styles.logoWrapper}>
            <img
              src="/images/millions-3d-logo.png"
              alt="MILLIONS Logo"
              className={styles.logo}
            />
          </div>

          <Text variant="h4" color="lights-high" className={styles.title}>
            Become A MILLIONS Creator
          </Text>

          <Text
            variant="body1Regular16"
            color="lights-medium"
            className={styles.subtitle}
          >
            Get brand deals, create products, host interviews, and more.
          </Text>

          <div className={styles.actions}>
            {renderGoProButton()}
            {renderLearnMoreButton()}
          </div>
        </div>

        {/* Desktop Layout */}
        <div
          className={cn(styles.desktopLayout, isInline && styles.hideDesktop)}
        >
          <div className={styles.desktopContent}>
            <div className={styles.logoColumn}>
              <img
                src="/images/millions-3d-logo.png"
                alt="MILLIONS Logo"
                className={styles.logo}
              />
            </div>

            <div className={styles.textColumn}>
              <Text variant="h4" color="lights-high" className={styles.title}>
                Become A MILLIONS Creator
              </Text>

              <Text
                variant="body1Regular16"
                color="lights-medium"
                className={styles.subtitle}
              >
                Get brand deals, create products, host interviews, and more.
              </Text>
            </div>

            <div className={styles.actionsColumn}>
              {renderGoProButton()}
              {renderLearnMoreButton()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BecomeACreatorCTA;
